/* eslint-disable */

import { PreprocessorCompileError, TokenizerCompileError } from "./ElfCompileErrors";

type TCharacterTypes = 'STRING' | 'COMMENT' | 'ESCAPE' | 'SPACE' | 'CHAR' | 'DIGIT' | 'SYMBOL';
type TCharacterSourcePosition = { line: number; column: number; index: number; };
type TCharacterBase<T extends TCharacterTypes> = { ch: string, __todo_filePath?: never /*when we want to include multiple files (file[line:col] error resolving)*/, line: number, column: number, type: T };
type TCharacterString = TCharacterBase<'STRING'> & { source: TCharacterSourcePosition };
type TCharacterComment = TCharacterBase<'COMMENT'> & { source: TCharacterSourcePosition };
type TCharacterEscape = TCharacterBase<'ESCAPE'>;
type TCharacterSpace = TCharacterBase<'SPACE'>;
type TCharacterChar = TCharacterBase<'CHAR'>;
type TCharacterDigit = TCharacterBase<'DIGIT'>;
type TCharacterSymbol = TCharacterBase<'SYMBOL'>;
export type TCharacter = TCharacterString | TCharacterComment | TCharacterEscape | TCharacterSpace | TCharacterChar | TCharacterDigit | TCharacterSymbol;


export function normalizeLineEndings(input: string): string {
    return input.replace(/\r?\n/g, "\n");
}

// its REQUIRED that input uses normalized line endings "\n" instead "\r\n".
export function preprocess(input: string): TPreprocessorResult {
    const characters: TCharacter[] = [];
    try {
        // ascii range 0-256 are allowed inside code!
        // but inside comments any character range can be used.
        /*
         console.log(`[${new Array(256).fill().map((_,__)=>String.fromCharCode(__)).slice(0,256).join('')}]`)
         123456789:;<=>?@ABCDEFGHIJKLMNOPQRSTUVWXYZ[\]^_`abcdefghijklmnopqrstuvwxyz{|}~¡¢£¤¥¦§¨©ª«¬­®¯°±²³´µ¶·¸¹º»¼½¾¿ÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÖ×ØÙÚÛÜÝÞßàáâãäåæçèéêëìíîïðñòóôõö÷øùúûüýþÿ
        */
        const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz".split('');
        const digits = "0123456789".split('');
        const spaces = "\t\n ".split('');
        const tokenMatcherMap: { [character: string]: { type: Extract<TCharacterTypes, 'CHAR' | 'DIGIT' | 'SPACE' | 'SYMBOL'> } } = {};
        for (const entry of chars) tokenMatcherMap[entry] = { type: 'CHAR' };
        for (const entry of digits) tokenMatcherMap[entry] = { type: 'DIGIT' };
        for (const entry of spaces) tokenMatcherMap[entry] = { type: 'SPACE' };
        // everything else from ascii (that is not chars, digits or spaces - mentioned above) is a SYMBOL
        for (let asciiCode = 0; asciiCode < 256; asciiCode++) {
            const ch = String.fromCharCode(asciiCode);
            if (!tokenMatcherMap[ch]) {
                tokenMatcherMap[ch] = { type: 'SYMBOL' };
            }
        }
        let line = 1;
        let column = 0;
        let runningComment = false;
        let runningString = false;
        let escapeNext = false;
        let commentPos: TCharacterSourcePosition = { line, column, index: 0 };
        let stringPos: TCharacterSourcePosition = { line, column, index: 0 };
        for (let i = 0; i < input.length; i++) {
            const ch: string = input[i]!;
            const nextCh: string | undefined = input[i + 1];
            ++column;

            if (!runningString && !runningComment) {
                if (ch === "~") {
                    if (column === 1) {
                        const lineComment: TCharacterSourcePosition = { line, column, index: i };
                        while (i < input.length) {
                            const ch = input[i]!;
                            characters.push({ ch, line, column, type: "COMMENT", source: lineComment })
                            if (ch === "\n") {
                                ++line;
                                column = 0;
                                break;
                            }
                            ++column;
                            ++i;
                        }
                        continue;
                    } else {
                        throw new PreprocessorCompileError(`Celo vrstični komentar je lahko samo v prvem stolpcu vsake vrstice`, { line, column }, { line, column })
                    }
                }
            }
            // [Notice: Do not add character consuming code above this line, as its handling escape sequence!]
            if (runningString) {
                if (escapeNext) {
                    switch (ch) {
                        case '\\':
                            characters.push({ ch, line, column, type: "STRING", source: { ...stringPos } });
                            break;
                        case '`':
                            characters.push({ ch, line, column, type: "STRING", source: { ...stringPos } });
                            break;
                        case 'n':
                            characters.push({ ch: '\n', line, column, type: "STRING", source: { ...stringPos } });
                            break;
                        case 't':
                            characters.push({ ch: '\t', line, column, type: "STRING", source: { ...stringPos } });
                            break;
                        case '0':
                            characters.push({ ch: '\0', line, column, type: "STRING", source: { ...stringPos } });
                            break;
                        default: {
                            throw new PreprocessorCompileError(`Ubežni znak "\\" ne more biti apliciran na znak "${ch}"`, { line, column }, { line, column });
                        }
                    }
                    escapeNext = false;
                    continue;
                } else if (ch === "\\") {
                    escapeNext = true;
                    characters.push({ ch: '\\', line, column, type: "ESCAPE" });
                    continue;
                }
            }

            if (runningComment) {
                if (escapeNext) {
                    characters.push({ ch, line, column, type: "COMMENT", source: { ...commentPos } });
                    escapeNext = false;
                    continue;
                } else if (ch === "\\") {
                    escapeNext = true;
                    // even though \ is an escape character, when in comments it can be treated as comment
                    // as individually it is still meant to be a character.
                    // we dont mind as its going to be consumed as a comment.
                    // the only effect of escaping is that it can escape end of comment!
                    if (nextCh === ")") {
                        characters.push({ ch, line, column, type: "ESCAPE" });
                    }
                    characters.push({ ch, line, column, type: "COMMENT", source: { ...commentPos } });
                    continue;
                }
            }

            if (ch === "\n") {
                if (runningComment) {
                    characters.push({ ch, line, column, type: "COMMENT", source: { ...commentPos } })
                } else if (runningString) {
                    characters.push({ ch, line, column, type: "STRING", source: { ...stringPos } })
                } else {
                    characters.push({ ch, line, column, type: "SPACE" });
                }
                ++line;
                column = 0;
                continue;
            }

            if (!runningComment && !runningString && ch === "`") {
                runningString = true;
                stringPos.line = line;
                stringPos.column = column;
                stringPos.index = i;
            }

            if (!runningComment && !runningString && ch === "(") {
                runningComment = true;
                commentPos.line = line;
                commentPos.column = column;
                commentPos.index = i;
            }

            if (ch === ")" && !runningComment && !runningString) {
                // prevent from using ) outside of comments, strings or ascii numbers
                throw new PreprocessorCompileError(`Ni možno zaključiti komentarja, saj ta ne obstaja!`, { line, column }, { line, column });
            }

            if (runningComment) {
                //[note] we dont need to check for character type in comments, can be utf if user wants, it gets deleted anyway at AST compilation.
                characters.push({ ch, line, column, type: "COMMENT", source: { ...commentPos } });
                if (ch === ")") runningComment = false;
                continue;
            }

            if (runningString) {
                if (!tokenMatcherMap[ch]) { // "strings support only asci!"
                    throw new PreprocessorCompileError(`Nepodprti znak "${ch}"`, { line, column }, { line, column });
                }
                characters.push({ ch, line, column, type: "STRING", source: { ...stringPos } });
                // as open and close are same symbols, we use index to distinguish if operating on the same character.
                // that started it.
                if (ch === "`" && i !== stringPos.index) runningString = false;
                continue;
            }

            const match = tokenMatcherMap[ch];
            if (!match) {
                throw new PreprocessorCompileError(`Nepodprti znak "${ch}"`, { line, column }, { line, column });
            }
            characters.push({ ch, line, column, type: match.type });
        }

        if (runningComment) {
            throw new PreprocessorCompileError(`Komentar ni zaključen!`, commentPos, { line, column });
        }

        if (runningString) {
            throw new PreprocessorCompileError(`Niz znakov ni zaključen!`, stringPos, { line, column });
        }

        return { characters };
    } catch (err) {
        if (err instanceof PreprocessorCompileError) {
            throw new PreprocessFailedException(err.message, { characters }, err);
        }
        throw err;
    }
}


export function wrappedPreprocessor(input: string): [ReturnType<(typeof preprocess)>, PreprocessFailedException | null] {
    try {
        const result = preprocess(input);
        return [result, null];
    } catch (err) {
        if (err instanceof PreprocessFailedException) {
            return [err.partialResult, err];
        }
        throw err;
    }
}

export type TPreprocessorResult = {
    characters: TCharacter[],
}

export class PreprocessFailedException extends Error {
    constructor(public message: string, public partialResult: TPreprocessorResult, public compileError: PreprocessorCompileError) {
        super(message);
    }
}


type TTokenTypes = 'STRING' | 'SPACE' | 'KEYWORD' | 'LABEL' | 'NUMBER' | 'REGISTER' | 'SYMBOL' | 'ASCII' | 'ESCAPE' | 'COMMENT';
type TTokenBase<T extends TTokenTypes> = { token: string, __todo_filePath?: never /*when we want to include multiple files (file[line:col] error resolving)*/, line: number, column: number, lastLine: number, lastColumn: number, type: T };
type TTokenString = TTokenBase<'STRING'> & { value: string };
type TTokenSpace = TTokenBase<'SPACE'>;
type TTokenKeyword = TTokenBase<'KEYWORD'>;
type TTokenLabel = TTokenBase<'LABEL'> & { assumedType?: 'FUNCTION' | 'CONSTANT' };
export type TTokenAscii = TTokenBase<'ASCII'> & { stringToken: TTokenString, charCode: number };
type TTokenNumber = TTokenBase<'NUMBER'>;
export type TTokenSymbol = TTokenBase<'SYMBOL'>;
type TTokenRegister = TTokenBase<'REGISTER'> & { value: string };
/* not used in ast builder */
export type TTokenComment = TTokenBase<'COMMENT'>;
export type TTokenEscape = TTokenBase<'ESCAPE'>;
/* ----------------------- */
export type TToken = TTokenString | TTokenSpace | TTokenKeyword | TTokenLabel | TTokenNumber | TTokenSymbol | TTokenRegister | TTokenAscii;


export function endLineCol(token: TToken): { line: number, column: number } {
    // lastColumn points always at the beginning of last character, if we want to range it,
    // we must add +1., if this is not true, then the token must be fixed, and NOT this method.
    return { line: token.lastLine, column: token.lastColumn };
}

export function tokenize(charactersIn: TCharacter[]): TTokenizeResult {
    function extractEscapes<T extends TCharacter>(characters: T[]): {
        escapes: TCharacterEscape[],
        rest: Exclude<T, TCharacterEscape>[]
    } {
        const rest = characters.filter(char => char.type !== "ESCAPE") as Exclude<T, TCharacterEscape>[];
        const escapes = characters.filter(char => char.type === "ESCAPE") as TCharacterEscape[]
        return { rest, escapes }
    }

    function transformCommentsToSpaces<T extends TCharacter>(characters: T[]): {
        transformed: Exclude<T, TCharacterComment>[],
        original: T[]
    } {
        const original = [...characters];
        const transformed = characters.map(ch => ch.type === 'COMMENT' ? { ...ch, type: 'SPACE' } : ch) as Exclude<T, TCharacterComment>[];
        return { transformed, original };
    }

    function parseDigits(characters: TCharacter[], line: number, column: number): TTokenNumber {
        let token = '';
        let previous: TCharacter | null = null;
        let usedSeparator = false;
        let lastLine: number = line;
        let lastColumn: number = column;
        const firstCharToken = characters[0];
        let hasDigit = false;
        const isPrefixed = ['+', '-'].includes(firstCharToken.ch);
        if (isPrefixed) {
            token += firstCharToken.ch;
            lastLine = firstCharToken.line;
            lastColumn = firstCharToken.column;
            previous = firstCharToken;
            characters.shift();
        }

        while (characters.length) {
            const next = characters[0]!;
            switch (next.type) {
                case 'DIGIT':
                    const prefixLength = isPrefixed ? 1 : 0;
                    // dont allow prefixed 0s
                    if (token[prefixLength] === "0" && token.length === (1 + prefixLength)) {
                        throw new TokenizerCompileError(`"${token}${next.ch}" je neveljaven zapis števila`, { line, column }, next);
                    }
                    hasDigit = true;
                    token += next.ch;
                    lastLine = next.line;
                    lastColumn = next.column;
                    previous = next;
                    characters.shift();
                    break;
                case 'SPACE':
                    if (!hasDigit) {
                        throw new TokenizerCompileError(`"${token}" je neveljaven zapis števila`, { line, column }, { line: lastLine, column: lastColumn });
                    }
                    return { token, type: 'NUMBER', line, column, lastLine, lastColumn };
                case 'SYMBOL':
                    if (next.ch === ".") {
                        if (usedSeparator || previous?.type !== 'DIGIT') {
                            throw new TokenizerCompileError(`"${token}${next.ch}" je neveljaven zapis števila`, { line, column }, next);
                        }
                        previous = next;
                        characters.shift();
                        const nextAfter = characters[0];
                        token += next.ch;
                        lastLine = next.line;
                        lastColumn = next.column;
                        usedSeparator = true;
                        if (nextAfter?.type !== 'DIGIT') {
                            throw new TokenizerCompileError(`"${token}${nextAfter?.ch ?? ''}"! je neveljaven zapis števila`, { line, column }, { line: lastLine, column: lastColumn });
                        }
                        break;
                    }
                    if (!hasDigit) {
                        throw new TokenizerCompileError(`"${token}" je neveljaven zapis števila`, { line, column }, { line: lastLine, column: lastColumn });
                    }
                    return { token, type: 'NUMBER', line, column, lastLine, lastColumn };
                default:
                    throw new TokenizerCompileError(`"${token}${next.ch}" je neveljaven zapis števila`, { line, column }, { line: lastLine, column: lastColumn });
            }
        }

        if (!hasDigit) {
            throw new TokenizerCompileError(`"${token}" je neveljaven zapis števila`, { line, column }, { line: lastLine, column: lastColumn });
        }
        return { token, type: 'NUMBER', line, column, lastLine, lastColumn };
    }

    function parseChars(characters: TCharacter[], line: number, column: number): TTokenLabel | TTokenKeyword {

        function isChrUpper(chr: string): boolean {
            return "A" <= chr && chr <= "Z"
        }
        // IF UPPER CASE: KEYWORD ELSE LABEL
        let token = '';
        let lastLine: number = line;
        let lastColumn: number = column;
        const firstChar = characters[0]!.ch;
        let type: TTokenLabel['type'] | TTokenKeyword['type'] = isChrUpper(firstChar) ? 'KEYWORD' : 'LABEL';
        while (characters.length) {
            const next = characters[0]!;
            switch (next.type) {
                case 'CHAR':
                    if (!isChrUpper(next.ch)) type = 'LABEL';
                    token += next.ch;
                    lastLine = next.line;
                    lastColumn = next.column;
                    characters.shift();
                    break;
                case 'DIGIT':
                    token += next.ch;
                    lastLine = next.line;
                    lastColumn = next.column;
                    characters.shift();
                    break;
                case 'SPACE':
                    return { token, type, line, column, lastLine, lastColumn };
                case 'SYMBOL':
                    // support underscores in CHAR sequences
                    if (next.ch === "_") {
                        token += next.ch;
                        lastLine = next.line;
                        lastColumn = next.column;
                        characters.shift();
                        break;
                    }
                    return { token, type, line, column, lastLine, lastColumn };
                default:
                    throw new TokenizerCompileError(`"${token}${next.ch}" je neveljaven zapis niza`, { line, column }, { line: lastLine, column: lastColumn });
            }
        }
        return { token, type, line, column, lastLine, lastColumn };
    }

    function parseSpace(characters: TCharacter[], line: number, column: number): TTokenSpace {
        let lastColumn = column;
        let lastLine = line;
        let token = '';
        while (true) {
            const next = characters[0];
            if (next?.type !== 'SPACE') {
                return { token, type: 'SPACE', line, column, lastLine, lastColumn };
            }
            token += next.ch;
            lastColumn = next.column;
            lastLine = next.line;
            characters.shift();
        }
    }

    function parseStringChunks(characters: TCharacter[], line: number, column: number): TTokenString {
        const stringIndex = (characters[0]! as TCharacterString).source.index;
        let lastLine: number = line;
        let lastColumn: number = column;
        let chunks = [];
        while (true) {
            const next = characters[0];
            if (!next || next.type !== "STRING" || next.source.index !== stringIndex) {
                const token = chunks.join('');
                // remove enclosing ` 
                chunks.shift();
                chunks.pop();
                const value = chunks.join('');
                return { token, value, type: 'STRING', line, column, lastLine, lastColumn };
            }
            chunks.push(next.ch);
            characters.shift();
            lastLine = next.line;
            lastColumn = next.column;
        }
    }

    function parseSymbol(characters: TCharacter[], line: number, column: number): TTokenSymbol {
        const next = characters.shift()!;
        return { token: next.ch, type: 'SYMBOL', line, column, lastLine: next.line, lastColumn: next.column };
    }

    function parseRegister(characters: TCharacter[], line: number, column: number): TTokenRegister {
        const firstPeek = characters[0];
        if (firstPeek?.type !== 'SYMBOL' || firstPeek.ch !== '@') {
            throw new TokenizerCompileError(`"${firstPeek?.ch}" je neveljaven zapis registra`, { line, column }, { line, column });
        }
        characters.shift();
        let token = firstPeek.ch;
        let lastLine = firstPeek.line;
        let lastColumn = firstPeek.column;
        let registerValue: string = "";

        const leadingDigit = characters[0];
        if (leadingDigit?.type === 'DIGIT' && leadingDigit.ch === "0") {
            throw new TokenizerCompileError(`"${token}${leadingDigit.ch}" je neveljaven zapis registra`, { line, column }, { line: lastLine, column: lastColumn });
        }

        while (true) {
            const peek = characters[0];
            if (peek?.type !== 'DIGIT') {
                const value = registerValue || "0";
                return { token, type: 'REGISTER', line, column, lastLine, lastColumn, value };
            }

            lastLine = peek.line;
            lastColumn = peek.column;
            token += peek.ch;
            registerValue += peek.ch;

            characters.shift();
        }
    }

    function parseComment(characters: TCharacter[], line: number, column: number): TTokenComment {
        let lastColumn = column;
        let lastLine = line;
        let token = '';
        while (true) {
            const next = characters[0];
            if (next?.type !== 'COMMENT') {
                return { token, type: 'COMMENT', line, column, lastLine, lastColumn };
            }
            token += next.ch;
            lastColumn = next.column;
            lastLine = next.line;
            characters.shift();
        }
    }

    function parseEscape(characters: TCharacter[], line: number, column: number): TTokenEscape {
        const next = characters.shift()!;
        return { token: next.ch, type: 'ESCAPE', line, column, lastLine: next.line, lastColumn: next.column };
    }

    function parseAscii(characters: TCharacter[], line: number, column: number): TTokenAscii {
        let token = '';
        const sym = parseSymbol(characters, line, column);
        token += sym.token;
        const next = characters[0];
        if (!next) {
            throw new TokenizerCompileError(`"${token}" je neveljaven ascii vrednostni zapis`, { line, column }, { eof: true });
        }
        if (next.type !== "STRING") {
            throw new TokenizerCompileError(`"${token}${next.ch ?? ''}" je neveljaven ascii vrednostni zapis`, { line, column }, next);
        }
        const stringToken = parseStringChunks(characters, line, column);
        if (stringToken.value.length > 1) {
            throw new TokenizerCompileError(`Pričakoval znak, vendar dobil niz "${stringToken.value}"`, { line, column }, stringToken);
        }
        token += stringToken.token;
        const charCode = stringToken.value.length === 0 ? 0 : stringToken.value.charCodeAt(0);
        return { type: 'ASCII', charCode, line, column, lastLine: stringToken.lastLine, lastColumn: stringToken.lastColumn, token, stringToken };
    }

    // used for highlighting
    const filteredCharactersWithEscape = extractEscapes(charactersIn)
    // used for highlighting
    const filteredCharactersWithComments = transformCommentsToSpaces(filteredCharactersWithEscape.rest);
    // no escapes or comments.
    const characters = filteredCharactersWithComments.transformed;

    const tokens: TToken[] = [];
    const commentTokens: TTokenComment[] = [];
    const escapeTokens: TTokenEscape[] = [];
    try {
        const escapes = filteredCharactersWithEscape.escapes;
        while (escapes.length) {
            const token = escapes[0]!;
            escapeTokens.push(parseEscape(escapes, token.line, token.column));
        }
        const originals = filteredCharactersWithComments.original;
        while (originals.length) {
            const next = originals[0]!;
            if (next.type === 'COMMENT') {
                commentTokens.push(parseComment(originals, next.line, next.column));
            } else {
                originals.shift();
            }
        }

        while (characters.length) {
            const peek = characters[0]!;
            const { line, column } = peek;
            switch (peek.type) {
                case 'SPACE':
                    tokens.push(parseSpace(characters, line, column));
                    break;
                case 'SYMBOL':
                    switch (peek.ch) {
                        case '+':
                        case '-': {
                            tokens.push(parseDigits(characters, line, column));
                            break;
                        }
                        case '@': {
                            tokens.push(parseRegister(characters, line, column));
                            break;
                        }
                        case '#': {
                            tokens.push(parseAscii(characters, line, column));
                            break;
                        }
                        default: {
                            tokens.push(parseSymbol(characters, line, column));
                            break;
                        }
                    }
                    break;
                case 'DIGIT':
                    tokens.push(parseDigits(characters, line, column));
                    break;
                case 'CHAR':
                    tokens.push(parseChars(characters, line, column));
                    break;
                case 'STRING':
                    tokens.push(parseStringChunks(characters, line, column));
                    break;
                default:
                    const notAllTokensCovered: never = peek;
                    throw new Error(`Unhandled case: ${(notAllTokensCovered as TCharacter).type}`);
            }
        }


    } catch (err) {
        if (err instanceof TokenizerCompileError) {
            throw new TokenizeFailedException(err.message, {
                astReadyTokens: [...tokens],
                syntaxHighlightTokens: [...tokens, ...commentTokens, ...escapeTokens]
            }, err);
        }
        console.error('UNEXPECTED ERROR!', err)
        throw err;
    }

    const result: TTokenizeResult = {
        astReadyTokens: tokens,
        syntaxHighlightTokens: [...tokens, ...commentTokens, ...escapeTokens]
    }
    return result;
}

// SHOULD NEVER THROW!!! Primary usecase is allow advanced hinting, but if it fails
// we dont care.
export function postprocessTokenization(result: TTokenizeResult) {
    /*** BEGIN LABEL COLORIZATION ***/
    // use ast tokens, as comments should be removed from our lookups,
    // we care only about "ast" syntax at this point
    const tokens = result.astReadyTokens;
    // DETECT ALL FUNCTION AND CONST INITIALIZATIONS and mark them!
    const tokenIndexMap = tokens.map((t, i) => [t, i] as [TToken, number]);
    const tokenKeywordIndexPair = tokenIndexMap.filter(([t]) => t.type === 'KEYWORD')
    for (const [kwToken, kwIndex] of tokenKeywordIndexPair) {
        switch (kwToken.token) {
            case 'FUNCTION':
            case 'CONSTANT':
                const spaceToken = tokens[kwIndex + 1];
                const labelToken = tokens[kwIndex + 2];
                if (spaceToken?.type !== 'SPACE') break;
                if (labelToken?.type !== 'LABEL') break;
                labelToken.assumedType = kwToken.token;
                break;
            default:
                break;
        }
    }

    // MARK USAGES OF FUNCTIONS OR CONSTS
    for (const [token, index] of tokenIndexMap.filter(([t]) => t.type === 'LABEL')) {
        const labelToken = (token as TTokenLabel);
        if (labelToken.assumedType) continue;

        const isFnCallLabel = tokens[index - 1]?.type === 'SPACE' &&
            tokens[index - 2]?.type === 'KEYWORD' &&
            tokens[index - 2]?.token === "CALL";

        (token as TTokenLabel).assumedType = isFnCallLabel ? 'FUNCTION' : 'CONSTANT';
    }
    /*** END LABEL COLORIZATION ***/
}

export function wrappedTokenizer(charactersIn: TCharacter[]): [ReturnType<(typeof tokenize)>, TokenizeFailedException | null] {
    try {
        const result = tokenize(charactersIn);
        return [result, null];
    } catch (err) {
        if (err instanceof TokenizeFailedException) {
            return [err.partialResult, err];
        }
        throw err;
    }
}

export type TTokenizeResult = {
    astReadyTokens: TToken[],
    syntaxHighlightTokens: (TToken | TTokenComment | TTokenEscape)[]
}

export class TokenizeFailedException extends Error {
    constructor(public message: string, public partialResult: TTokenizeResult, public compileError: TokenizerCompileError) {
        super(message);
    }
}